import { getPlanFeaturesForPlan, pricingEditionFor, type PlanFeatures, type PricingEdition } from '@highlight/common'
import { useUnifiedBilling } from '../store/global-billing'
import { useLdFlag } from './useLdFlag'

/** LaunchDarkly key for the October 2026 pricing revamp; the gateway reads the same flag. */
const FREE_TIER_PLAN_REVAMP_FLAG = 'v2_free_tier_plan_revamp'

/** The pricing edition this user is on. Off until LaunchDarkly says otherwise, matching the gateway's fail-closed read. */
export function usePricingEdition(): PricingEdition {
  return pricingEditionFor(useLdFlag<boolean>(FREE_TIER_PLAN_REVAMP_FLAG, false))
}

/** LaunchDarkly key that starts the revamp's heads-up to 2.0 subscribers (FUN-87). Desktop only. */
const PLAN_REVAMP_ANNOUNCEMENT_FLAG = 'v2_plan_revamp_announcement'

/** Whether the sidepanel may announce the revamp. Off until LaunchDarkly says otherwise. */
export function usePlanRevampAnnounced(): boolean {
  return useLdFlag<boolean>(PLAN_REVAMP_ANNOUNCEMENT_FLAG, false)
}

/**
 * Per-plan feature limits for the v2 pricing tiers, mirroring the backend gates.
 * Gates on the raw billed plan (the identity the server enforces on), never on
 * useEffectiveBillingPlan, whose presentation swap would wrongly gate legacy
 * pro users. Legacy plans resolve to every feature enabled.
 */
export function usePlanFeatures(): PlanFeatures {
  const { subscription } = useUnifiedBilling()
  return getPlanFeaturesForPlan(subscription.plan, usePricingEdition())
}

/**
 * Whether the plan lets the user pick the chat model. Reads `paywall.plan`, not
 * `subscription.plan`: with no billing profile the subscription has no plan
 * (every feature) while the paywall reads it as Free.
 */
export function useModelChoice(): boolean {
  const { paywall } = useUnifiedBilling()
  return getPlanFeaturesForPlan(paywall.plan, usePricingEdition()).modelChoice
}

/**
 * Whether meeting summaries need a paid plan: they do on Free, except on the
 * revamp, whose Free includes them. Reads the same plan feature main's summary
 * gate does, so the desktop never withholds a summary this says it has.
 */
export function useSummariesNeedUpgrade(): boolean {
  return !usePlanFeatures().meetingSummaries
}
